import { BomTableUI } from "./BomTableUI";
import { DownloadCsv } from "./DownloadCsv";
import { DragResizeModal } from "../ContextMenu/Modals/DragResizeModal";

/**
 * Manages the Bill of Materials (BOM) table functionality, including UI interactions,
 * drag-and-resize modal behavior, and event handling.
 * @category Components
 * @subcategory BOM
 */
export class BomTable {
  /**
   * Creates an instance of BomTable.
   * @param {Object} experience - The experience object containing the event hub.
   * @param {Object} experience.eventHub - Event hub for emitting and listening to events.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.csvDownloader = new DownloadCsv();
    this.bomTableUI = new BomTableUI(this.csvDownloader);
    this.modalDiv = this.bomTableUI.createModal();
    document.body.appendChild(this.modalDiv);
    this.dragResizeModal = null;
    this.setupEventListeners();
    this.initializeUI();
  }

  /**
   * Sets up event listeners for BOM table interactions.
   * Listens to 'bom-switch' and 'bomUpdated' events from the event hub.
   */
  setupEventListeners() {
    this.eventHub.on("bom-switch", this.handleBomSwitchEvent.bind(this));
    this.eventHub.on("bomUpdated", this.updateBom.bind(this));
  }

  cleanup() {
    // 1. Remove event listeners from eventHub
    this.eventHub.off("bom-switch", this.handleBomSwitchEvent);
    this.eventHub.off("bomUpdated", this.updateBom);
  
    // 2. Remove appended DOM elements
    if (this.modalDiv && document.body.contains(this.modalDiv)) {
      document.body.removeChild(this.modalDiv);
    }
  
    // 3. Dispose of DragResizeModal instance
    if (this.dragResizeModal && typeof this.dragResizeModal.dispose === "function") {
      this.dragResizeModal.dispose();
    }
  
    // 4. Clear UI and Component Instances
    this.bomTableUI = null;
    this.dragResizeModal = null;
    this.modalDiv = null;
  
    // 5. Nullify Bound Functions
    this.handleBomSwitchEvent = null;
    this.updateBom = null;
  
    // 6. Log cleanup
    console.log("BomTable resources have been cleaned up.");
  }

  /**
   * Initializes the UI components for the BOM table.
   * Hides the download button and displays a default alert indicating no components are added.
   */
  initializeUI() {
    document.getElementById("bomTable-download-button").style.display = "none";
    const alertDiv = document.getElementById("bomTable-bom-alert");
    alertDiv.style.display = "block";
    alertDiv.textContent = "No components added";
  }

  /**
   * Handles the 'bom-switch' event to toggle the visibility of the BOM modal.
   * @param {Object} e - Event object containing the `enabled` flag.
   * @param {boolean} e.enabled - Whether the BOM modal should be enabled (visible).
   */
  handleBomSwitchEvent(e) {
    const isEnabled = e.enabled;
    this.modalDiv.style.display = isEnabled ? "flex" : "none";
    if (isEnabled) {
      // Correct position if modal is underneath the header
      this.correctModalPosition();
      // Initialize drag and resize functionality only if the modal is visible
      setTimeout(() => {
        this.initializeDragResize();
      }, 0);
      // Emit 'sceneUpdated' event to notify other parts of the application
      this.eventHub.emit("xmd");
    }
  }

  /**
   * Corrects the modal position if it's positioned underneath the header.
   * Ensures the modal is always accessible and not hidden behind the header.
   */
  correctModalPosition() {
    const header = document.getElementById("catHeader");
    if (header && this.modalDiv) {
      const headerRect = header.getBoundingClientRect();
      const modalRect = this.modalDiv.getBoundingClientRect();
      const minTop = headerRect.bottom;
      
      // If modal's top edge is below the header's bottom edge, reposition it
      if (modalRect.top < minTop) {
        this.modalDiv.style.top = `${minTop}px`;
      }
    }
  }

  /**
   * Initializes drag-and-resize functionality for the BOM modal.
   * Ensures the functionality is initialized only once and only when the modal is visible.
   */
  initializeDragResize() {
    // Ensure initialization happens only once and modal is displayed
    if (!this.dragResizeModal && this.modalDiv.style.display !== "none") {
      // Find draggableContainer inside the modal
      const draggableContainer = this.modalDiv.querySelector(
        ".bomTable-draggable-container"
      );

      // Pass draggableContainer as the drag handle and modalDiv as the target
      this.dragResizeModal = new DragResizeModal(
        draggableContainer,
        this.modalDiv
      );
    }
  }

  /**
   * Updates the BOM table with new data or clears it if no data is available.
   * @param {Object} bomData - The BOM data to update the table with.
   * @param {Array<Object>} bomData.data - Array of BOM table rows.
   * @param {Array} bomData.total - Total values for the BOM table.
   */
  updateBom(bomData) {
    const hasData = bomData && bomData.data.length > 0;
    this.setDownloadButtonVisibility(hasData);
    this.setAlertVisibility(hasData);

    if (hasData) {
      this.bomTableUI.updateTable(bomData);
      this.bomTableUI.updateFooter(bomData.total);
    } else {
      this.bomTableUI.clearTable();
    }
  }

  /**
   * Sets the visibility of the download button.
   * @param {boolean} visible - Whether the download button should be visible.
   */
  setDownloadButtonVisibility(visible) {
    document.getElementById("bomTable-download-button").style.display = visible
      ? "block"
      : "none";
  }

  /**
   * Sets the visibility of the alert message indicating no components are added.
   * @param {boolean} visible - Whether the alert should be visible.
   */
  setAlertVisibility(visible) {
    document.getElementById("bomTable-bom-alert").style.display = visible
      ? "none"
      : "block";
  }
}
/*   triggerCheckbox() {
    const checkbox = document.getElementById("bom-switch");
    checkbox.checked = !checkbox.checked;
  }
} */
